import { HeartPulse, ClipboardList, FileCheck2, AlertTriangle, Stethoscope, ActivitySquare, ListChecks, Timer, ShieldCheck } from "lucide-react";
import { StakeholderPage } from "./StakeholderPage";

/**
 * /for/athletic-trainers — the AT's purchase-decision page.
 * ATs buy triage leverage, objective RTP, and defensible documentation.
 */
export default function ForAthleticTrainers() {
  return (
    <StakeholderPage
      testid="for-at-page"
      eyebrow="For Athletic Trainers"
      EyebrowIcon={HeartPulse}
      headline="One of you. Two hundred of them."
      headlineSub="Triage should not run on hallway conversations."
      intro={<>
        BioHacker Sports™ gives the athletic training room <b className="text-white">a continuously ranked triage queue</b> —
        who is trending toward injury, who is stalling in rehab, whose return-to-play is objective rather than optimistic.
        Every observation is time-stamped, structured, and defensible.
      </>}
      risksHeading="The four failure modes of an overloaded training room."
      risksSub="And how structured load data retires each one."
      risks={[
        { icon: AlertTriangle, label: "Reactive care instead of surveillance", tone: "#F43F5E",
          body: "With one AT per hundreds of athletes, care starts when the athlete limps in. The pre-injury window — where soft-tissue outcomes are actually changeable — passes unobserved.",
          relief: "Automated workload + recovery surveillance across the full roster surfaces the top-risk athletes daily. You spend your hours where the risk actually lives." },
        { icon: Timer, label: "Return-to-play by calendar, not capacity", tone: "#F59E0B",
          body: "\"Week four, he should be ready\" is how re-injuries happen. Without objective baselines, RTP is a negotiation between an eager athlete and a nervous coach.",
          relief: "Pre-injury baselines — sprint splits, jump metrics, biomechanics — give every RTP decision a measurable bar: return at 95% of verified baseline, not at week four." },
        { icon: FileCheck2, label: "Documentation gaps that become liability", tone: "#A78BFA",
          body: "Handwritten notes and memory don't survive a records request. When an injury turns litigious, the question is always: what did you know, and when?",
          relief: "Every wellness entry, workload flag, and intervention is structured, dated, and exportable. Duty-of-care evidence accumulates automatically, SOC 2 + HIPAA-aligned." },
        { icon: Stethoscope, label: "Coach-AT friction on availability calls", tone: "#22D3EE",
          body: "You say hold him, the coach says play him, and the argument is opinion versus opinion in front of the athlete.",
          relief: "Shared objective dashboard ends the standoff — the workload spike, the recovery deficit, and the asymmetry are on screen for both of you." },
      ]}
      surfaceHeading="Your training-room surface."
      surfaceSub="Triage, rehab, and RTP on one screen."
      surface={[
        { icon: ListChecks, label: "Ranked triage queue", body: "Roster sorted by composite risk — workload spike × recovery deficit × flagged mechanics. Your morning starts with the five athletes who need you most." },
        { icon: ActivitySquare, label: "Rehab trajectory tracking", body: "Test-retest history against pre-injury baselines. Plateaus are visible in days, and progressions are documented without extra paperwork." },
        { icon: ShieldCheck, label: "Defensible record trail", body: "Structured, time-stamped entries for every screen, flag, and clearance — the documentation standard your license and your institution both need." },
      ]}
      ctaHeading="Give your training room surveillance-grade coverage."
      ctaSub="Book an AT-scoped walkthrough. We'll show the triage queue running on a full roster."
      demoLabel="Request an AT demo"
    />
  );
}
